import 'styles/pages/index.scss';
import '~scripts/main';

// Packages
import Vue from 'vue';
import eases from 'eases';

// Swiper
import Swiper from 'swiper';
import { Autoplay, EffectFade, Navigation as SwiperNavigation, Pagination as SwiperPagination } from 'swiper/modules';
Swiper.use([Autoplay, EffectFade, SwiperNavigation, SwiperPagination]);

// Core
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import { scrollAnimationProgress } from '~scripts/core/animations';
import { isInViewport } from '~scripts/core/helpers';
import scrollTo from '~scripts/core/scrollTo';

// Blocks
import Header from '~scripts/blocks/header';
import Navigation from '~scripts/blocks/navigation';
import ClientsLogotypes from '~scripts/blocks/vue/ClientsLogotypes.vue';
import initRequestForm from '~scripts/blocks/request-form';
import '~scripts/blocks/events/forum-2024/forum-results';
import '~scripts/blocks/events/forum-2024/modal-video';

// Components
import '~scripts/components/modals';
import SubscribeForm from '~scripts/components/vue/forms/SubscribeForm.vue';
import initTextAnimation from '~scripts/components/text-animation';

// Breakpoints
const mediaSm = window.matchMedia(`(max-width: ${BREAKPOINTS.smMax})`);
const mediaMd = window.matchMedia(`(min-width: ${BREAKPOINTS.mdMin})`);
const mediaLg = window.matchMedia(`(min-width: ${BREAKPOINTS.lgMin})`);
const mediaXl = window.matchMedia(`(min-width: ${BREAKPOINTS.xlMin})`);

// Storage
const storage = window.localStorage;

// Variables
const region = window.region;

//
// HEADER
//
Header();

//
// NAVIGATION
//
Navigation();

//
// NEW ALERT
//
const newAlertEl = document.getElementById('newAlert');

if (newAlertEl) {
  const newAlertData = newAlertEl.dataset.alert;
  const newAlertCloseEl = document.getElementById('newAlertClose');

  if (!storage.getItem(newAlertData) || storage.getItem(newAlertData) !== 'none') {
    newAlertEl.style.display = 'block';
  }

  newAlertCloseEl.addEventListener('click', (e) => {
    e.preventDefault();
    storage.setItem(newAlertData, 'none');
    newAlertEl.style.display = 'none';
  });
}

//
// HUNTFLOW BLOCK
//
const handleHuntflowBlockAnimation = () => {
  const huntflowBlockAnimation = document.getElementById('huntflowBlockAnimation');
  const huntflowBlockWindow = document.getElementById('huntflowBlockWindow');

  return scrollAnimationProgress(huntflowBlockAnimation, {
    getStart() {
      let start = 0;

      if (mediaMd.matches) {
        start = 250;
      }

      if (mediaLg.matches) {
        start = 150;
      }

      return start;
    },
    getEnd() {
      const height = huntflowBlockWindow.offsetHeight;
      const end = mediaSm.matches ? height * 1.25 : height;
      return end / 2 + 100;
    },
    lerpValue: 0.25,
  });
};

handleHuntflowBlockAnimation();
mediaSm.addEventListener('change', handleHuntflowBlockAnimation);
mediaMd.addEventListener('change', handleHuntflowBlockAnimation);
mediaLg.addEventListener('change', handleHuntflowBlockAnimation);
mediaXl.addEventListener('change', handleHuntflowBlockAnimation);

// Scroll to Huntflow AI
if (region !== 'ai') {
  const gotoAiEl = document.querySelector('.js-ai-link');

  const handleGotoAi = () => {
    const textingEl = document.querySelector('.js-huntflow-ai-texting');
    const windowHeight = window.innerHeight;

    gotoAiEl.addEventListener('click', (e) => {
      e.preventDefault();

      const y = textingEl.getBoundingClientRect().top + window.scrollY - windowHeight / 2;

      scrollTo(y);
    });
  };

  handleGotoAi();
  window.addEventListener('resize', handleGotoAi);
}

//
// HUNTFLOW NEW
//
if (region !== 'ai') {
  const handleHuntflowNewAnimation = () => {
    const huntflowNewEl = document.getElementById('huntflowNew');
    scrollAnimationProgress(
      huntflowNewEl,
      {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - (window.innerHeight + el.offsetHeight / 4);
        },
        getEnd(el, start) {
          return start + window.innerHeight - el.offsetHeight / 2;
        },
        lerpValue: 0.25,
      },
      eases.expoOut
    );
  };

  handleHuntflowNewAnimation();
}

//
// CLIENTS
//
new Vue({
  el: '#VClientsLogotypes',
  name: 'VClientsLogotypes',
  components: {
    ClientsLogotypes,
  },
});

//
// HUNTFLOW AI
//
if (region !== 'ai') {
  // Animation: background
  const handlehuntflowAiAnimation = () => {
    const huntflowAiEl = document.getElementById('huntflowAi');

    return scrollAnimationProgress(huntflowAiEl, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.scrollY - window.innerHeight;
      },
      getEnd(el, start) {
        return start + 400;
      },
      lerpValue: 0.25,
    });
  };

  handlehuntflowAiAnimation();

  // Animation: ten years factoids
  const handleTenYearsFactoidsAnimation = () => {
    const huntflowAiCloudsEl = document.getElementById('tenYearsFactoids');

    return scrollAnimationProgress(huntflowAiCloudsEl, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        const topOffset = rect.top + window.scrollY;

        return topOffset - window.innerHeight;
      },
      getEnd(el) {
        const rect = el.getBoundingClientRect();
        const topOffset = rect.top + scrollY;
        const elementHeight = el.offsetHeight;

        return topOffset + elementHeight / 2 - window.innerHeight / 2;
      },
    });
  };

  handleTenYearsFactoidsAnimation();

  // Animation: clouds
  const handlehuntflowCloudsAnimation = () => {
    const huntflowAiCloudsEl = document.getElementById('huntflowAiClouds');

    return scrollAnimationProgress(huntflowAiCloudsEl, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.scrollY - window.innerHeight + 100;
      },
      getEnd(el, start) {
        return start + 1000;
      },
      lerpValue: 0.25,
    });
  };

  handlehuntflowCloudsAnimation();

  // Animation: texting
  initTextAnimation('.js-huntflow-ai-texting');

  // Animation: whirling
  let huntflowAiWhirlingAnimation = null;

  const handleHuntflowAiWhirlingAnimation = () => {
    const huntflowAiWhirlingEl = document.getElementById('huntflowAiWhirling');

    const huntflowAiWhirlingFrames = document.getElementById('huntflowAiWhirlingFrames');
    const frames = Array.from(huntflowAiWhirlingFrames.querySelectorAll('img'));

    let prevFrame = null;

    if (mediaSm.matches) {
      if (huntflowAiWhirlingAnimation && typeof huntflowAiWhirlingAnimation === 'function') {
        huntflowAiWhirlingAnimation();
      }
    } else {
      huntflowAiWhirlingAnimation = scrollAnimationProgress(huntflowAiWhirlingEl, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          const imgHeight = el.querySelector('img').offsetHeight;

          return rect.top + window.scrollY - (window.innerHeight - imgHeight);
        },
        getEnd(el, start) {
          const imgHeight = el.querySelector('img').offsetHeight;

          return start + (el.offsetHeight - imgHeight);
        },
        onProgress(progress) {
          const i = Math.round(progress * (frames.length - 1));
          if (prevFrame) {
            prevFrame.style.opacity = '0';
          }
          prevFrame = frames[i];
          prevFrame.style.opacity = '1';
        },
        lerpValue: 0.25,
      });
    }
  };

  handleHuntflowAiWhirlingAnimation();
  mediaSm.addEventListener('change', handleHuntflowAiWhirlingAnimation);

  // Animation: contents

  const handleHuntflowAiContentsAnimation = () => {
    const huntflowAiContentsEl = document.getElementById('huntflowAiContents');
    scrollAnimationProgress(
      huntflowAiContentsEl,
      {
        getStart(el) {
          if (mediaSm.matches) {
            const windowHeight = window.innerHeight;
            const blockTop = el.getBoundingClientRect().top + window.scrollY;

            return blockTop - windowHeight / 1.5;
          } else {
            const rect = el.getBoundingClientRect();
            const offset = mediaSm.matches ? 375 : 250;

            return rect.top + window.scrollY - (window.innerHeight - offset);
          }
        },
        getEnd(el, start) {
          if (mediaSm.matches) {
            const blockTop = el.getBoundingClientRect().top + window.scrollY;
            return blockTop;
          } else {
            return start + 250;
          }
        },
        lerpValue: 0.25,
      },
      eases.expoOut
    );
  };

  handleHuntflowAiContentsAnimation();
  mediaSm.addEventListener('change', handleHuntflowAiContentsAnimation);

  // Video: contents
  const huntflowAiContentsVideoEl = document.getElementById('huntflowAiContentsVideo');

  const handleHuntflowAiContentVideo = () => {
    const huntflowAiContentsVideo = huntflowAiContentsVideoEl.querySelector('video');
    const huntflowAiContentsVideoOffset = huntflowAiContentsVideo.offsetHeight / 2;

    document.addEventListener('scroll', () => {
      if (isInViewport(huntflowAiContentsVideo, true, huntflowAiContentsVideoOffset)) {
        if (huntflowAiContentsVideo.currentTime === 0) {
          huntflowAiContentsVideo.play();
        }
      } else {
        huntflowAiContentsVideo.pause();
        huntflowAiContentsVideo.currentTime = 0;
      }
    });
  };

  handleHuntflowAiContentVideo();
  mediaSm.addEventListener('change', handleHuntflowAiContentVideo);
}

//
// KEY FEATURES
//
//
// Sliders
//
const keyFeaturesSliderOptionsBase = {
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
};
const keyFeaturesSliderOptionsSm = {
  allowTouchMove: true,
  effect: 'slide',
  loop: false,
  navigation: false,
  noSwiping: false,
  slidesPerView: 'auto',
  spaceBetween: 12,
};
const keyFeaturesSliderOptionsMd = {
  allowTouchMove: false,
  effect: 'fade',
  loop: true,
  navigation: false,
  noSwiping: true,
  slidesPerView: 1,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
  spaceBetween: 0,
};

let keyFeaturesSlider = null;

const handleKeyFeaturesSliderInit = () => {
  if (keyFeaturesSlider) {
    keyFeaturesSlider.forEach((slider) => {
      slider.destroy();
    });
  }

  const options = mediaSm.matches ? keyFeaturesSliderOptionsSm : keyFeaturesSliderOptionsMd;

  keyFeaturesSlider = new Swiper('.js-key-features-slider', Object.assign(keyFeaturesSliderOptionsBase, options));
};

// Tabs
const ymid = window.ymid;

const keyFeaturesTabsList = document.querySelectorAll('.js-key-features-tabs');

const keyFeaturesSliderSetActiveTab = (tabs, tab = tabs[0]) => {
  tabs.forEach((tab) => {
    if (tab.classList.contains('is-active')) {
      tab.classList.remove('is-active');
    }
  });

  tab.classList.add('is-active');

  const caret = tabs[0].querySelector('span');
  caret.style.transform = `translateX(${Math.floor(tab.offsetLeft)}px)`;
  caret.style.width = `${Math.floor(tab.offsetWidth)}px`;
};

const handleKeyFeaturesTabsInit = () => {
  // Tabs
  keyFeaturesTabsList.forEach((tabs, index) => {
    const keyFeaturesTabs = tabs.querySelectorAll('li');
    const keyFeaturesTabsIndex = index;

    keyFeaturesTabs.forEach((tab, index) => {
      const tabIndex = index;

      keyFeaturesSliderSetActiveTab(keyFeaturesTabs, tab[0]);

      tab.addEventListener('click', (e) => {
        e.preventDefault();

        keyFeaturesSliderSetActiveTab(keyFeaturesTabs, tab);

        keyFeaturesSlider[keyFeaturesTabsIndex].slideTo(tabIndex);
      });
    });
  });

  // Slide change
  keyFeaturesSlider.forEach((slider, index) => {
    const keyFeaturesTabs = keyFeaturesTabsList[index].querySelectorAll('li');

    slider.on('destroy', () => {
      keyFeaturesSliderSetActiveTab(keyFeaturesTabs);
    });

    slider.on('slideChange', (swiper) => {
      const slideIndex = swiper.activeIndex;
      const slideEvent = swiper.slides[slideIndex].dataset.eventSlide;

      // Events
      if (slideEvent) {
        try {
          dataLayer.push({ event: 'gaEvent', eventcategory: slideEvent, eventaction: 'click' });
        } catch (e) {
          // output is not needed
        }

        try {
          ym(ymid, 'reachGoal', slideEvent, {});
        } catch (e) {
          // output is not needed
        }
      }

      // Tabs
      keyFeaturesTabs.forEach((tab, index) => {
        const tabIndex = index;

        if (tabIndex === slideIndex) {
          keyFeaturesSliderSetActiveTab(keyFeaturesTabs, tab);
        }
      });
    });
  });
};

// Resize
const handleKeyFeaturesResize = () => {
  const keyFeaturesTabsList = document.querySelectorAll('.js-key-features-tabs');

  keyFeaturesTabsList.forEach((tabs) => {
    const keyFeaturesTabs = tabs.querySelectorAll('li');

    const caret = keyFeaturesTabs[0].querySelector('span');

    keyFeaturesTabs.forEach((tab) => {
      if (tab.classList.contains('is-active')) {
        caret.style.transform = `translateX(${Math.floor(tab.offsetLeft)}px)`;
        caret.style.width = Math.floor(tab.offsetWidth) + 'px';
      }
    });
  });
};

handleKeyFeaturesSliderInit();
mediaSm.addEventListener('change', handleKeyFeaturesSliderInit);

handleKeyFeaturesTabsInit();
mediaSm.addEventListener('change', handleKeyFeaturesTabsInit);

window.addEventListener('resize', handleKeyFeaturesResize);

//
// Animations
//
// Badges
let keyFeaturesBadgesAnimation = [];

const handleKeyFeaturesBadgesAnimation = () => {
  const keyFeaturesBadges = document.querySelectorAll('.js-key-features-badge');

  if (mediaSm.matches) {
    keyFeaturesBadgesAnimation.forEach((stop) => stop());
  } else {
    keyFeaturesBadgesAnimation = Array.from(keyFeaturesBadges, (keyFeaturesBadge) => {
      return scrollAnimationProgress(keyFeaturesBadge, {
        getStart(el) {
          const headerOffset = mediaSm.matches ? 64 : 72;
          const rect = el.getBoundingClientRect();
          return rect.top + (window.scrollY - headerOffset);
        },
        getEnd(el, start) {
          return start + window.innerHeight / 1.66;
        },
        lerpValue: 0.25,
      });
    });
  }
};

handleKeyFeaturesBadgesAnimation();
mediaSm.addEventListener('change', handleKeyFeaturesBadgesAnimation);

// Wrapper
let keyFeaturesWrapperAnimation = [];

const handleKeyFeaturesWrapperAnimation = () => {
  const keyFeaturesWrappers = document.querySelectorAll('.js-key-features-wrapper');

  if (mediaSm.matches) {
    keyFeaturesWrapperAnimation.forEach((stop) => stop());
  } else {
    keyFeaturesWrapperAnimation = Array.from(keyFeaturesWrappers, (keyFeaturesWrapper) => {
      return scrollAnimationProgress(
        keyFeaturesWrapper,
        {
          getStart(el) {
            const rect = el.getBoundingClientRect();
            return rect.top + window.scrollY - window.innerHeight * 1.33;
          },
          getEnd(el, start) {
            return start + window.innerHeight * 0.66;
          },
          lerpValue: 0.25,
        },
        eases.expoIn
      );
    });
  }
};

handleKeyFeaturesWrapperAnimation();
mediaSm.addEventListener('change', handleKeyFeaturesWrapperAnimation);

//
// CUSTOMER FEEDBACKS
//
import customerFeedbacksInit from '~scripts/blocks/customer-feedbacks';

customerFeedbacksInit();

//
// DATA SAFETY
//
if (region !== 'ai') {
  let dataSafetyItemsAnimation = [];

  const handleDataSafetyItemsAnimation = () => {
    const dataSafetyItems = document.querySelectorAll('.js-data-safety-item');

    if (mediaSm.matches) {
      dataSafetyItemsAnimation.forEach((stop) => stop());
    } else {
      dataSafetyItemsAnimation = Array.from(dataSafetyItems, (dataSafetyItem) => {
        return scrollAnimationProgress(dataSafetyItem, {
          getStart(el) {
            const rect = el.getBoundingClientRect();
            return rect.top + window.scrollY - window.innerHeight;
          },
          getEnd(el, start) {
            const height = el.offsetHeight;
            return start + height;
          },
        });
      });
    }
  };

  handleDataSafetyItemsAnimation();
  mediaSm.addEventListener('change', handleDataSafetyItemsAnimation);
}

//
// ENTERPRISE
//
let enterpriseItemsAnimation = null;

const handleEnterpriseItemsAnimation = () => {
  const enterpriseItems = document.getElementById('enterpriseItems');

  if (mediaSm.matches) {
    if (enterpriseItemsAnimation && typeof enterpriseItemsAnimation === 'function') {
      enterpriseItemsAnimation();
    }
  } else {
    enterpriseItemsAnimation = scrollAnimationProgress(enterpriseItems, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.scrollY - (window.innerHeight - 110);
      },
      getEnd(el, start) {
        return start + (window.innerHeight * 1.75 + 110 + 72);
      },
    });
  }
};

handleEnterpriseItemsAnimation();
mediaSm.addEventListener('change', handleEnterpriseItemsAnimation);

//
// BENEFITS
//
if (region !== 'ai') {
  let benefitsItemsAnimation = [];

  const handleBenefitsItemsAnimation = () => {
    const benefitsItems = document.querySelectorAll('.js-benefits-item');

    if (mediaSm.matches) {
      benefitsItemsAnimation.forEach((stop) => stop());
    } else {
      benefitsItemsAnimation = Array.from(benefitsItems, (benefitsItem) => {
        return scrollAnimationProgress(benefitsItem, {
          getStart(el) {
            const rect = el.getBoundingClientRect();
            return rect.top + window.scrollY - window.innerHeight;
          },
          getEnd(el, start) {
            const height = el.offsetHeight;
            return start + height * 0.725;
          },
        });
      });
    }
  };

  handleBenefitsItemsAnimation();
  mediaSm.addEventListener('change', handleBenefitsItemsAnimation);
}

//
// CUSTOMER CASES
//
import customerCasesInit from '~scripts/blocks/customer-cases';

if (region !== 'ai') {
  customerCasesInit();
}

//
// API SUPERPOWER
//
import apiSuperpowerInit from '~scripts/blocks/api-superpower';

apiSuperpowerInit();

//
// ENCYCLOPEDIA
//
if (region !== 'ai') {
  const body = document.body;
  const main = document.getElementById('main');
  const wrapper = document.getElementById('wrapper');
  const encyclopedia = document.getElementById('encyclopedia');

  const scroll = {
    handle() {
      if (window.scrollY > wrapper.offsetHeight) {
        requestAnimationFrame(() => {
          body.style.paddingBottom = null;
        });

        encyclopedia.style.position = 'static';
      } else {
        body.style.paddingBottom = `${encyclopedia.offsetHeight}px`;
        encyclopedia.style.position = 'fixed';
      }
    },
    on() {
      this.handle();

      document.addEventListener('scroll', this.handle);
      window.addEventListener('resize', this.handle);
    },
    off() {
      body.style.paddingBottom = null;
      encyclopedia.style.position = 'static';

      document.removeEventListener('scroll', this.handle);
      window.removeEventListener('resize', this.handle);
    },
  };

  // Scroll
  window.addEventListener('DOMContentLoaded', () => {
    if (!mediaSm.matches) {
      scroll.on();
    }

    mediaSm.addEventListener('change', () => {
      if (!mediaSm.matches) {
        scroll.on();
      } else {
        scroll.off();
      }
    });
  });

  window.addEventListener('load', () => {
    if (!mediaSm.matches) {
      scroll.handle();
    }
  });

  // Animation
  let encyclopediaAnimation = null;

  const handleEncyclopediaAnimation = () => {
    if (mediaSm.matches) {
      if (encyclopediaAnimation && typeof encyclopediaAnimation === 'function') {
        encyclopediaAnimation();
      }
    } else {
      encyclopediaAnimation = scrollAnimationProgress(main, {
        getStart() {
          const rect = wrapper.getBoundingClientRect();
          return rect.bottom + (window.scrollY - window.innerHeight);
        },
        getEnd(el, start) {
          return start + window.innerHeight - 72;
        },
        lerpValue: 1,
      });
    }
  };

  handleEncyclopediaAnimation();
  mediaSm.addEventListener('change', handleEncyclopediaAnimation);
}

//
// DEMO REQUEST FORM
//
const demoRequestForms = document.querySelectorAll('.js-demo-request-form');

demoRequestForms.forEach((form) => {
  initRequestForm(form);
});

//
// SUBSCRIBE FORM
//
if (region !== 'ai') {
  const formWrapper = document.querySelector('.js-form-wrapper');

  new Vue({
    el: formWrapper,
    components: {
      SubscribeForm,
    },
  });
}
